import React from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';

const partners = [
  { 
    name: 'Chatslytics', 
    logoPath: '/logos/ChatsLyticsLogoRBG.png',
    logoFallback: 'Chatslytics',
    featured: true // Mark as featured for special treatment
  },
  { 
    name: 'IIM Lucknow', 
    logoPath: '/logos/Logo-IIML-EIC.png',
    logoFallback: 'IIML-EIC'
  },
  { 
    name: 'Google for Startups', 
    logoPath: '/logos/Logo_for_Google_for_Startups_page.png',
    logoFallback: 'Google'
  },
  { 
    name: 'Max Healthcare', 
    logoPath: '/logos/MAX.png',
    logoFallback: 'MAX'
  },
  { 
    name: 'Fifth Generation', 
    logoPath: '/logos/fifthgen.png',
    logoFallback: 'FifthGen'
  },
  { 
    name: 'Startup India', 
    logoPath: '/logos/startup-india.jpg',
    logoFallback: 'Startup India'
  },
  { 
    name: 'MedX', 
    logoPath: '/logos/medxlogo.jpg',
    logoFallback: 'MedX'
  },
  { 
    name: 'Mobi Armour', 
    logoPath: '/logos/mobi.png',
    logoFallback: 'Mobi'
  }
];

export const PartnersCarousel: React.FC = () => {
  return (
    <div id="partners-carousel-container" className="w-full py-8">
      <motion.div
        id="partners-carousel-header"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <p className="text-center text-white/90 dark:text-foreground mb-8 font-semibold text-lg">
          Trusted by leading institutions and technology partners
        </p>
      </motion.div>

      <Swiper
        id="partners-carousel-swiper"
        modules={[Autoplay]}
        spaceBetween={24}
        slidesPerView={2}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        speed={800}
        loop={true}
        breakpoints={{
          640: {
            slidesPerView: 3,
            spaceBetween: 32,
          },
          768: {
            slidesPerView: 4,
            spaceBetween: 40,
          },
          1024: {
            slidesPerView: 5,
            spaceBetween: 48,
          },
          1280: {
            slidesPerView: 6,
            spaceBetween: 56,
          },
        }}
        className="partners-swiper"
      >
        {partners.map((partner, index) => (
          <SwiperSlide key={partner.name}>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ 
                scale: 1.1,
                y: -5,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              className="group cursor-pointer"
            >
              {/* Remove the background box/card styling */}
              <div className="relative flex flex-col items-center justify-center p-4 h-28 transition-all duration-300">
                {/* Partner Logo - Larger and cleaner */}
                <motion.div 
                  className={`${
                    partner.featured 
                      ? 'w-20 h-16' // Larger featured logo
                      : 'w-18 h-14'  // Standard logos
                  } flex items-center justify-center transition-all duration-300 overflow-hidden mb-2`}
                  whileHover={{ 
                    scale: 1.1,
                    y: -2
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={partner.logoPath} 
                    alt={`${partner.name} logo`}
                    className={`max-w-full max-h-full object-contain opacity-70 hover:opacity-100 transition-all duration-300 ${
                      partner.name === 'IIM Lucknow' 
                        ? 'dark:invert dark:brightness-100 dark:contrast-100' 
                        : ''
                    }`}
                    style={{ 
                      filter: partner.name === 'IIM Lucknow' 
                        ? 'brightness(0.8) contrast(1.1) saturate(0.9)'
                        : 'brightness(0.8) contrast(1.1) saturate(0.9)',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                    loading="lazy"
                    onError={(e) => {
                      // Clean fallback without boxes
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="flex items-center justify-center w-full h-full">
                            <span class="text-sm font-semibold text-muted-foreground opacity-70 text-center px-2 leading-tight">
                              ${partner.logoFallback}
                            </span>
                          </div>
                        `;
                      }
                    }}
                  />
                </motion.div>
                
                {/* Partner name with improved typography */}
                <motion.span 
                  className={`text-xs font-medium transition-all duration-300 text-center leading-tight ${
                    partner.featured 
                      ? 'text-white/90 dark:text-foreground font-semibold'
                      : 'text-white/70 dark:text-muted-foreground group-hover:text-white dark:group-hover:text-foreground'
                  }`}
                  whileHover={{ y: -1 }}
                >
                  {partner.name}
                </motion.span>
              </div>
            </motion.div>
          </SwiperSlide>
        ))}
      </Swiper>

      <style>{`
        .partners-swiper .swiper-slide {
          height: auto;
          display: flex;
          align-items: center;
        }
        
        .partners-swiper {
          padding: 16px 0;
          overflow: visible;
        }
        
        .partners-swiper .swiper-wrapper {
          align-items: center;
        }
        
        /* Clean hover effects */
        .partners-swiper .swiper-slide img {
          transition: all 0.3s ease;
        }
        
        /* Responsive adjustments */
        @media (max-width: 640px) {
          .partners-swiper {
            padding: 12px 0;
          }
        }
      `}</style>
    </div>
  );
};
